"use client";

import { OccasionalWiggle } from "@/components/about/occasional-wiggle";
import React, { useMemo, useRef, useState } from "react";

type IconSpec = {
  src: string; // image url
  depth: number; // 0..1 where 1 is NEAR (moves more, appears larger and more opaque)
  pos: [number, number]; // normalized [x,y] within section 0..1
  href: string; // link target
};

type FeaturedGamesSectionProps = {
  showPlayMathButton?: boolean;
};

export function FeaturedGamesSection({ showPlayMathButton = true }: FeaturedGamesSectionProps) {
  const icons = useMemo<IconSpec[]>(
    () => [
      {
        src: "/images/marketing/home/games/DiceMerge.webp",
        depth: 0.63,
        pos: [0.72, 0.4],
        href: "/games/2048-dice",
      },
      {
        src: "/images/marketing/home/games/DominoesPro.webp",
        depth: 0.45,
        pos: [0.79, 0.56],
        href: "/games/dominoes-pro",
      },
      {
        src: "/images/marketing/home/games/MonopolyJr.webp",
        depth: 1.0,
        pos: [0.35, 0.35],
        href: "/games/monopoly-jr",
      },
      {
        src: "/images/marketing/home/games/SliceFractions.webp",
        depth: 0.85,
        pos: [0.52, 0.27],
        href: "/games/mm-slice-fractions",
      },
      {
        src: "/images/marketing/home/games/StarStuff.webp",
        depth: 0.85,
        pos: [0.4, 0.7],
        href: "/games/star-stuff",
      },
      {
        src: "/images/marketing/home/games/SnakebirdMath.webp",
        depth: 0.75,
        pos: [0.64, 0.67],
        href: "/games/snakebird-math",
      },
      {
        src: "/images/marketing/home/games/Triangles.webp",
        depth: 0.2,
        pos: [0.18, 0.8],
        href: "/games/triangles",
      },
      {
        src: "/images/marketing/home/games/Checkers.webp",
        depth: 0.22,
        pos: [0.88, 0.43],
        href: "/games/checkers",
      },
      {
        src: "/images/marketing/home/games/TicTacToePro.webp",
        depth: 0.4,
        pos: [0.09, 0.65],
        href: "/games/tic-tac-toe-pro",
      },
      {
        src: "/images/marketing/home/games/Multiplication.webp",
        depth: 0.35,
        pos: [0.16, 0.4],
        href: "/games/mm-multiplication",
      },
      {
        src: "/images/marketing/home/games/Sudoku.webp",
        depth: 0.2,
        pos: [0.73, 0.15],
        href: "/games/sudoku",
      },
      {
        src: "/images/marketing/home/games/UnrollBall.webp",
        depth: 0.2,
        pos: [0.9, 0.3],
        href: "/games/unroll-ball-slider",
      },
      {
        src: "/images/marketing/home/games/WaterSort.webp",
        depth: 0.42,
        pos: [0.22, 0.58],
        href: "/games/water-sort-puzzle",
      },
    ],
    []
  );

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const mouseRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const rafRef = useRef<number | null>(null);
  const maxShiftPx = 50; // adjust for overall parallax strength
  const [hovered, setHovered] = useState<number | null>(null);
  // Controls how small icons appear at depth → 0. Increase to make far icons larger.
  const minScaleAtZeroDepth = 0.35;
  // Upper bound when depth → 1. Lower this if close icons feel too big.
  const maxScaleAtOneDepth = 1.5;
  // Track container width to size icons relative to container width
  const [containerWidth, setContainerWidth] = useState(0);
  // Base visual size for icons before depth scaling, as % of container width (e.g., 0.08 = 8%)
  const baseIconSizePct = 0.08;
  // Hover scale multiplier for icons
  const hoverScale = 1.12;
  // Subtle rocking animation
  const rockDurationMs = 3600; // base period

  React.useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      setContainerWidth(rect.width);
    };
    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, []);

  // Imperative parallax update using CSS variables to avoid per-mousemove React renders
  React.useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    el.style.setProperty("--mx", "0px");
    el.style.setProperty("--my", "0px");
    const onMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      const nx = (e.clientX - rect.left) / rect.width - 0.5; // -0.5..0.5
      const ny = (e.clientY - rect.top) / rect.height - 0.5;
      mouseRef.current = { x: nx * maxShiftPx, y: ny * maxShiftPx };
      if (rafRef.current == null) {
        rafRef.current = requestAnimationFrame(() => {
          rafRef.current = null;
          el.style.setProperty("--mx", `${mouseRef.current.x}px`);
          el.style.setProperty("--my", `${mouseRef.current.y}px`);
        });
      }
    };
    el.addEventListener(
      "mousemove",
      onMove as EventListener,
      { passive: true } as AddEventListenerOptions
    );
    return () => {
      el.removeEventListener("mousemove", onMove as EventListener);
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [maxShiftPx]);
  const iconsSorted = useMemo(() => {
    return [...icons].sort((a, b) => a.depth - b.depth); // far -> near
  }, [icons]);

  return (
    <section>
      <div ref={wrapperRef} className="w-full relative" style={{ aspectRatio: "1440 / 600" }}>
        {/* Background image */}
        <img
          src="/images/marketing/home/games/planet.webp"
          alt=""
          className="absolute inset-0 w-full h-full object-contain"
          style={{ display: "block" }}
        />

        {/* Icon overlay */}
        <div className="absolute inset-0">
          <style jsx>{`
            @keyframes pm-icon-rock {
              0% { transform: rotate(2.5deg); }
              50% { transform: rotate(-2.5deg); }
              100% { transform: rotate(2.5deg); }
            }
          `}</style>
          {iconsSorted.map((icon, i) => {
            const [px, py] = icon.pos;
            const baseLeft = `${px * 100}%`;
            const baseTop = `${py * 100}%`;
            const depthClamped = Math.max(0, Math.min(1, icon.depth));
            const parallaxFactor = depthClamped; // higher depth => closer => more movement
            const scale =
              minScaleAtZeroDepth + (maxScaleAtOneDepth - minScaleAtZeroDepth) * depthClamped; // closer looks bigger
            const baseSizePx = Math.max(1, Math.round(containerWidth * baseIconSizePct * scale));
            const sizePx = hovered === i ? Math.round(baseSizePx * hoverScale) : baseSizePx;
            const opacity = 0.4 + 0.6 * depthClamped; // closer is more opaque
            // Phase offset by X position; negative delay to start mid-cycle
            const phaseDelayMs = -Math.round(px * rockDurationMs);
            return (
              <a
                key={`${icon.src}-${i}`}
                href={icon.href}
                className="absolute block pointer-events-auto transition-all duration-150 ease-out"
                style={{
                  left: baseLeft,
                  top: baseTop,
                  // Use CSS variables for smooth, non-React parallax
                  transform:
                    "translate(-50%, -50%) translate(calc(var(--mx) * var(--pf)), calc(var(--my) * var(--pf)))",
                  // Per-icon parallax factor
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore vendor prop type
                  "--pf": parallaxFactor,
                  width: sizePx,
                  height: sizePx,
                  opacity: hovered === i ? 1 : opacity,
                }}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered((prev) => (prev === i ? null : prev))}
              >
                <img
                  src={icon.src}
                  alt="game icon"
                  className="select-none rounded-[12.5%] will-change-transform"
                  draggable={false}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    animation:
                      `pm-icon-rock ${rockDurationMs}ms ease-in-out infinite` as unknown as undefined,
                    animationDelay: `${phaseDelayMs}ms`,
                    transformOrigin: "50% 85%",
                  }}
                />
              </a>
            );
          })}

          {/* Center Play button - frontmost element with parallax at nearest depth (md+) */}
          {showPlayMathButton &&
            (() => {
              return (
                <div
                  className="absolute left-1/2 top-1/2 pointer-events-auto hidden md:block"
                  style={{
                    transform: "translate(-50%, -50%) translate(var(--mx), var(--my))",
                  }}
                >
                  <OccasionalWiggle>
                    <a
                      href="/games"
                      className="inline-block bg-orange-500 text-black px-10 sm:px-14 py-5 md:py-5 lg:py-7 rounded-full charlie-kingdom-font text-4xl md:text-4xl lg:text-5xl hover:bg-orange-400 shadow-cta-orange border-4 border-black relative rotate-[-3deg]"
                    >
                      <span className="relative -top-1">Play Math</span>
                    </a>
                  </OccasionalWiggle>
                </div>
              );
            })()}
        </div>
      </div>
      {/* Button below the icon area on small screens */}
      {showPlayMathButton && (
        <div className="md:hidden flex items-center justify-center mb-6">
          <OccasionalWiggle>
            <a
              href="/games"
              className="inline-block bg-orange-500 text-black px-10 sm:px-14 py-5 rounded-full charlie-kingdom-font text-4xl hover:bg-orange-400 shadow-cta-orange border-4 border-black relative rotate-[-3deg]"
            >
              <span className="relative -top-1">Play Math</span>
            </a>
          </OccasionalWiggle>
        </div>
      )}
    </section>
  );
}
